/**
 * NX logins: both of an NX server's logins in one place, and for the other
 * servers of its NX site at once (Moorebank, 2026-10-20). The words and the
 * grouping are nx-logins.ts (tested); this only draws them and saves.
 *
 * The monitoring login is the asset's own credential: setting it here writes
 * that and nothing else of the asset, so nobody has to find its Edit tab.
 */

import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Drawer, Field } from './components/index.ts';
import { loginSummary, type LoginHost } from './nx-logins.ts';

interface CredentialOption {
  id: string;
  label: string;
  kind: string;
  username: string | null;
}

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));
const labelOf = (h: LoginHost): string => h.code ?? h.name;

export function NxLoginsDrawer<T extends LoginHost>({
  host,
  mates,
  siteName,
  onClose,
  onSaved,
}: {
  host: T;
  /** The other servers on the same NX site, offered for the same logins. */
  mates: T[];
  siteName: string | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [credentials, setCredentials] = useState<CredentialOption[] | null>(null);
  const [monitoring, setMonitoring] = useState(host.nxLogins.monitoring?.credentialId ?? '');
  const [elevated, setElevated] = useState(host.nxLogins.elevated?.credentialId ?? '');
  // Every server of the site is ticked: one site, one login, is the usual case.
  const [also, setAlso] = useState<Set<string>>(() => new Set(mates.map((m) => m.id)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ credentials: CredentialOption[] }>('/api/credentials')
      .then((r) => setCredentials(r.credentials.filter((c) => c.kind === 'userpass')))
      .catch((err: unknown) => setError(failure(err)));
  }, []);

  const targets = [host, ...mates.filter((m) => also.has(m.id))];
  const monitoringChanged = targets.some((t) => (t.nxLogins.monitoring?.credentialId ?? '') !== monitoring);
  const elevatedChanged = targets.some((t) => t.agentId !== null && (t.nxLogins.elevated?.credentialId ?? '') !== elevated);
  const unenrolled = targets.filter((t) => t.agentId === null);
  const sameAsElevated = monitoring !== '' && monitoring === elevated;

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (monitoringChanged) {
        await api.put('/api/agents/nx-logins', { assetIds: targets.map((t) => t.id), monitoring: monitoring === '' ? null : monitoring });
      }
      // The elevated login is kept with the agent: only enrolled servers take it.
      const enrolled = targets.filter((t) => t.agentId !== null);
      if (elevatedChanged && enrolled.length > 0) {
        await api.put('/api/agents/nx-logins', { assetIds: enrolled.map((t) => t.id), elevated: elevated === '' ? null : elevated });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const option = (c: CredentialOption): React.JSX.Element => (
    <option key={c.id} value={c.id}>
      {c.label}
      {c.username ? ` — ${c.username}` : ''}
    </option>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      title={`NX logins — ${labelOf(host)}`}
      subtitle="The NX users this server’s agent logs in as. Create the user in NX first, then save it under Settings › Credentials."
    >
      <div className="form-narrow stack-tight">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {(() => {
          const s = loginSummary(host);
          return (
            <Banner tone={s.problem ? 'warn' : 'ok'} title="Now">
              {s.text}
            </Banner>
          );
        })()}
        {credentials !== null && credentials.length === 0 && (
          <Banner tone="info" title="No logins saved yet">
            Add the NX user under <a href="#/settings/credentials">Settings › Credentials</a> as a username and password, then
            come back here.
          </Banner>
        )}
        <Field
          id="nx-monitoring"
          label="Monitoring login (used every minute)"
          hint="What the agent reads NX with all the time: cameras, alarms, disks. Give this NX user view-only rights (Site Health Viewers + Viewers)."
        >
          <select id="nx-monitoring" value={monitoring} onChange={(e) => setMonitoring(e.target.value)}>
            <option value="">None — CosMon cannot read NX</option>
            {credentials?.map(option)}
          </select>
        </Field>
        <Field
          id="nx-elevated-login"
          label="Elevated login (NX actions only)"
          hint={
            host.agentId === null
              ? 'Available once the agent is enrolled: it is kept with the agent.'
              : 'A Power User, handed to the agent only for an NX action you ask for, then forgotten. None: actions use the monitoring login.'
          }
        >
          <select id="nx-elevated-login" value={elevated} disabled={host.agentId === null} onChange={(e) => setElevated(e.target.value)}>
            <option value="">None — actions use the monitoring login</option>
            {credentials?.map(option)}
          </select>
        </Field>
        {monitoring === '' && elevated !== '' && (
          // The Moorebank mistake: the new login set as the elevated one only.
          <Banner
            tone="warn"
            title="Only an elevated login is set"
            actions={
              <Button size="sm" onClick={() => setMonitoring(elevated)}>
                Use it for monitoring too
              </Button>
            }
          >
            The elevated login is used only for NX actions. Without a monitoring login the agent cannot read NX at all.
          </Banner>
        )}
        {sameAsElevated && (
          <p className="small muted">
            The same login for both is fine for a single-login setup; the warning on the Hosts row says so if it can change NX.
          </p>
        )}
        {mates.length > 0 && (
          <>
            <strong className="small">
              Also on {siteName ?? 'this NX site'} ({mates.length} more server{mates.length === 1 ? '' : 's'})
            </strong>
            <ul className="check-list">
              {mates.map((m) => (
                <li key={m.id}>
                  <input
                    id={`nx-also-${m.id}`}
                    type="checkbox"
                    checked={also.has(m.id)}
                    onChange={(e) => {
                      const next = new Set(also);
                      if (e.target.checked) next.add(m.id);
                      else next.delete(m.id);
                      setAlso(next);
                    }}
                  />
                  <label htmlFor={`nx-also-${m.id}`}>
                    <span className="cell-strong">{labelOf(m)}</span>
                    <span className="small muted"> · {loginSummary(m).text}</span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
        {unenrolled.length > 0 && elevated !== '' && (
          <p className="small muted">
            {unenrolled.map(labelOf).join(', ')} {unenrolled.length === 1 ? 'is' : 'are'} not enrolled yet, so {unenrolled.length === 1 ? 'it gets' : 'they get'} the monitoring login only.
          </p>
        )}
        <div className="row-tight">
          <Button variant="primary" icon={<Save size={14} />} busy={busy} disabled={!monitoringChanged && !elevatedChanged} onClick={() => void save()}>
            {targets.length === 1 ? 'Save' : `Save for ${targets.length} servers`}
          </Button>
          <span className="small muted">The agent picks a new login up at its next report, within a minute.</span>
        </div>
      </div>
    </Drawer>
  );
}
